import { useLocalStorage } from '@mantine/hooks';
import React from 'react';
import { LuChevronDown, LuChevronRight } from 'react-icons/lu';
import styled from 'styled-components';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/utils/cn';
import { HorizontalLayout, Text } from 'widgets';

type NavGroupProps = {
    label: string;
    icon: JSX.Element;
    isCollapsed: boolean;
    storageKey: string;
    children: React.ReactNode;
};

export const NavGroup = ({ label, icon, isCollapsed, storageKey, children }: NavGroupProps) => {
    const [isOpen, setIsOpen] = useLocalStorage({ key: storageKey, defaultValue: false });

    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger asChild>
                <CollapsibleHeader $isCollapsed={isCollapsed} $isOpen={isOpen}>
                    <HorizontalLayout $alignItems="center" $gap={10}>
                        {icon}
                        {!isCollapsed && <Text $centered>{label}</Text>}
                    </HorizontalLayout>
                    {!isCollapsed && <ChevronIcon>{isOpen ? <LuChevronDown /> : <LuChevronRight />}</ChevronIcon>}
                </CollapsibleHeader>
            </CollapsibleTrigger>
            <CollapsibleContent className={cn({ 'pl-4': !isCollapsed })}>{children}</CollapsibleContent>
        </Collapsible>
    );
};

const CollapsibleHeader = styled.button<{ $isCollapsed: boolean; $isOpen: boolean }>`
    color: white;
    text-decoration: none;
    margin-top: 2px;
    margin-left: 10px;
    margin-right: 10px;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    padding: 11px 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    line-height: 1;
    user-select: none;
    background: transparent;
    border: none;
    width: calc(100% - 20px);
    cursor: pointer;

    svg {
        width: 18px;
        height: 18px;
    }

    &:hover {
        background: ${({ theme }) => theme.nav.highlightBackground};
    }

    ${({ $isCollapsed }) =>
        $isCollapsed &&
        `
        padding: 11px;
        justify-content: center;
    `}
`;

const ChevronIcon = styled.span`
    display: flex;
    align-items: center;
    transition: transform 0.2s ease;
`;
